import { responseStatus } from '../util/Fetch';
import CompanyDetails from './CompanyDetails';

export default class CompaniesSearch {
  /**
   * Companies Search
   *
   * @param {HTMLElement} searchElt  The search wrapper element
   * @param {HTMLElement} resultsElt  The search results wrapper element
   * @param {HTMLElement} detailsElt  The company details element
   * @param {CompaniesList|null} companiesList  The company list class
   * @param {number} maxResults  The maximum of results returned in total
   * @param {number} maxCategories  The maximum of categories returned
   */
  constructor(searchElt, resultsElt, detailsElt, companiesList, maxResults, maxCategories) {
    /**
     * @property {HTMLElement} searchElt  The search wrapper element
     */
    this.searchElt = searchElt;

    /**
     * @property {HTMLElement} resultsElt  The search results wrapper element
     */
    this.resultsElt = resultsElt;

    /**
     * @property {HTMLElement} detailsElt  The company details element
     */
    this.detailsElt = detailsElt;

    /**
     * @property {CompaniesList|null} companiesList  The company list class
     */
    this.companiesList = companiesList;

    /**
     * @property {number} maxResults  The maximum of results returned in total
     */
    this.maxResults = maxResults;

    /**
     * @property {number} maxCategories  The maximum of categories returned
     */
    this.maxCategories = maxCategories;

    /**
     * @property {HTMLElement} searchForm  The search form element
     */
    this.searchFormElt = searchElt.querySelector('.search-form');

    /**
     * @property {HTMLElement} searchInput  The search input element
     */
    this.searchInput = searchElt.querySelector('.search-input');

    /**
     * @property {HTMLElement} searchCategoryInput  The search category input element
     */
    this.searchCategoryInput = searchElt.querySelector('.search-category');

    /**
     * @property {Object[]} searchResults  The search results
     */
    this.searchResults = [];

    /**
     * @property {Object[]} searchCategoryResults  The search category results
     */
    this.searchCategoryResults = [];

    /**
     * @property {string} restUrl  The WP rest url for the companies
     */
    this.restUrl = this.searchInput.getAttribute('data-rest-url');

    /**
     * @property {string} restCategoryUrl  The WP rest url for the categories
     */
    this.restCategoryUrl = this.searchInput.getAttribute('data-rest-category-url');

    /**
     * @property {bool} shouldPopulateAfterSearch  Whether to populate the companies list after a search
     */
    this.shouldPopulateAfterSearch = false;

    /**
     * @property {bool} isCompanyMapOpen  Whether the company map is open
     */
    this.isCompanyMapOpen = false;

    /**
     * @property {number} transitionDuration  The .search-results CSS transition duration
     */
    this.transitionDuration = 350;

    /** Bind the events */
    this.bindEvents();

    /** Initialize */
    this.initialize();
  }

  /**
   * Bind the events
   */
  bindEvents() {
    this.onClickSearchResultHandler = this.onClickSearchResult.bind(this);
    this.onClickOutsideBox = this.hide.bind(this);
    this.bindKeyUp();
    this.bindSubmit();
    this.bindCategoryChange();
  }

  /**
   * Initialize
   */
  initialize() {
    /** Add the protocol to the rest url for local queries */
    if (document.querySelector('html').classList.contains('dev') && this.restUrl.indexOf('http') < 0) {
      this.restUrl = `http:${this.restUrl}`;
      this.restCategoryUrl = `http:${this.restCategoryUrl}`;
    }
  }

  /**
   * Bind the keyup event to the search input, the event handling is delayed for better performance
   */
  bindKeyUp() {
    let delayed = false;
    this.searchInput.addEventListener('keyup', e => {
      if (!delayed) {
        delayed = true;
        setTimeout(() => {
          delayed = false;
          if (this.searchInput.value.length >= 3 && e.key !== 'Enter' && e.keyCode !== 13) {
            this.searchCompanies(this.searchInput.value, this.searchCategoryInput.value);
          }
        }, 200);
      }
    });
  }

  /**
   * Bind the form submit event
   */
  bindSubmit() {
    this.searchElt.querySelector('button[type="submit"]').addEventListener('click', e => {
      e.preventDefault();
      e.currentTarget.blur();
      this.shouldPopulateAfterSearch = true;
      this.searchCompanies(this.searchInput.value, this.searchCategoryInput.value);
    });
  }

  /**
   * Bind the search category change event
   */
  bindCategoryChange() {
    this.searchCategoryInput.addEventListener('change', () => {
      this.shouldPopulateAfterSearch = true;
      this.searchCompanies(this.searchInput.value, this.searchCategoryInput.value);
    });
  }

  /**
   * Search companies, filtered by categories if the search category is not empty,
   * then search categories and populate the search results element.
   *
   * If the search query is empty (on click on a search result category),
   * search companies, filtered by category, then populate the companies list object.
   *
   * @param {String} searchQuery  The search query
   * @param {String} searchCategoryId  The search category id
   */
  searchCompanies(searchQuery, searchCategoryId) {
    // console.info(searchQuery, searchCategoryId);
    this.searchResults = [];
    let totalPages = 0;

    /** Set the rest url parameters with the search queries and the pagination */
    const restUrl = new URL(this.restUrl);
    const perPage = this.companiesList !== null ? this.companiesList.perPage : this.maxResults;
    restUrl.searchParams.append('per_page', String(perPage));
    if (searchQuery.length > 0) {
      restUrl.searchParams.append('search', searchQuery);
    }
    if (searchCategoryId.length > 0) {
      restUrl.searchParams.append('entreprise_categorie', searchCategoryId);
    }

    /** Fetch the results */
    fetch(restUrl, {
      method: 'GET',
    })
      .then(responseStatus)
      .then(response => {
        totalPages = parseInt(response.headers.get('X-WP-TotalPages'), 10);
        return response.json();
      })
      .then(response => {
        this.searchResults = response;
        if (searchQuery.length > 0 && !this.shouldPopulateAfterSearch) {
          if (searchCategoryId.length === 0 && this.companiesList !== null) {
            this.searchCategories(searchQuery);
          } else {
            this.populate();
          }
        }
        this.populateCompaniesList(searchQuery, searchCategoryId, response, totalPages);
      })
      .catch(error => console.warn(error.message));
  }

  /**
   * Populate the Companies list with the categories search results
   *
   * @param {string} searchQuery  The search query
   * @param {String} searchCategoryId  The search category id
   * @param {Object[]} results  The search / category search results
   * @param {number} totalPages  The total number of results pages
   */
  populateCompaniesList(searchQuery, searchCategoryId, results, totalPages) {
    if (this.companiesList !== null && (searchQuery.length === 0 || this.shouldPopulateAfterSearch)) {
      this.companiesList.searchQuery = searchQuery;
      this.companiesList.searchCategoryId = searchCategoryId;
      this.companiesList.currentPage = 1;
      this.companiesList.totalPages = totalPages;
      this.companiesList.shouldScrollIntoView = true;
      this.companiesList.unbindNavigation();
      this.companiesList.populate(results);
      this.companiesList.scrollToContent();
      this.shouldPopulateAfterSearch = false;

      /** Show / hide the company list title if the search query or category is not empty */
      if (this.companiesList.titleElt) {
        if (searchQuery.length > 0 || searchCategoryId.length > 0) {
          this.companiesList.titleElt.classList.add('is-hidden');
        } else {
          this.companiesList.titleElt.classList.remove('is-hidden');
        }
      }
    }
  }

  /**
   * Search categories and then populate the search results element
   * or populate the search results element without searching if the search category is not empty.
   *
   * @param {String} searchQuery  The search query
   */
  searchCategories(searchQuery) {
    this.searchCategoryResults = [];
    if (this.searchCategoryInput.value.length === 0) {
      /** Set the rest url parameters with the search query and the pagination */
      const restUrl = new URL(this.restCategoryUrl);
      restUrl.searchParams.append('search', searchQuery);
      restUrl.searchParams.append('per_page', this.maxCategories);

      /** Fetch the results */
      fetch(restUrl, {
        method: 'GET',
      })
        .then(responseStatus)
        .then(response => response.json())
        .then(response => {
          this.searchCategoryResults = response;
          this.populate();
        })
        .catch(error => console.warn(error.message));
    } else {
      /** Populate the results */
      this.populate();
    }
  }

  /**
   * Populate the search results element with the search results and category search results
   */
  populate() {
    const companiesElt = this.resultsElt.querySelector('.search-results-companies');
    const categoriesElt = this.resultsElt.querySelector('.search-results-categories');
    const maxResults = this.maxResults - this.searchCategoryResults.length;
    // console.info(this.searchResults, this.searchCategoryResults);

    /** Companies */
    companiesElt.innerHTML = '';
    if (this.searchResults.length > 0) {
      companiesElt.appendChild(this.renderResultsTitle(companiesElt.getAttribute('data-txt-title')));
      companiesElt.appendChild(this.renderResultsList(this.searchResults, maxResults, 'companies'));
    }

    /** Categories */
    categoriesElt.innerHTML = '';
    if (this.searchCategoryResults.length > 0) {
      categoriesElt.appendChild(this.renderResultsTitle(categoriesElt.getAttribute('data-txt-title')));
      categoriesElt.appendChild(this.renderResultsList(this.searchCategoryResults, maxResults, 'categories'));
    }

    /** Show / hide the search box */
    if (!this.shouldPopulateAfterSearch && (this.searchResults.length > 0 || this.searchCategoryResults.length > 0)) {
      this.show();
    } else {
      this.hide();
    }
  }

  /**
   * Render the search-results-title element
   *
   * @param {String} title  The title to display
   * @returns {HTMLElement}  h3.search-results-title
   */
  renderResultsTitle(title) {
    const resultsTitle = document.createElement('h3');
    resultsTitle.className = 'search-results-title';
    resultsTitle.innerHTML = title;
    return resultsTitle;
  }

  /**
   * Render the search-results-list element
   *
   * @param {Object[]} results  The search /category search results
   * @param {number} maxResults  The maximum number or results to display
   * @param {String} type 'companies' | 'categories'  The type of elements displayed
   * @returns {HTMLElement}  ul.search-results-list>li*n>a
   */
  renderResultsList(results, maxResults, type) {
    const resultsList = document.createElement('ul');
    resultsList.className = 'search-results-list';
    for (const result of results.slice(0, maxResults)) {
      const listItem = document.createElement('li');
      const listItemLink = document.createElement('a');
      listItemLink.href = '#';
      listItemLink.innerHTML = result.title !== undefined ? result.title.rendered : result.name;
      listItemLink.setAttribute('data-id', String(result.id));
      listItemLink.setAttribute('data-type', type);
      listItemLink.addEventListener('click', this.onClickSearchResultHandler);
      listItem.appendChild(listItemLink);
      resultsList.appendChild(listItem);
    }
    return resultsList;
  }

  /**
   * Click on a search result hanler
   *
   * @param {MouseEvent} e
   */
  onClickSearchResult(e) {
    e.preventDefault();
    this.hide();
    const type = e.currentTarget.getAttribute('data-type');
    const id = parseInt(e.currentTarget.getAttribute('data-id'), 10);
    let result = null;

    if (type === 'companies') {
      result = this.searchResults.find(result => result.id === id);
      const hasMap =
        typeof result.acf.gps_latitude === 'number' &&
        result.acf.gps_latitude > 0 &&
        typeof result.acf.gps_longitude === 'number' &&
        result.acf.gps_longitude > 0;

      if (!this.isCompanyMapOpen || !hasMap) {
        /** Open the company details */
        new CompanyDetails(result._links.self[0].href, this.detailsElt);
      } else {
        /** Populate the companies list */
        this.shouldPopulateAfterSearch = true;
        this.populateCompaniesList(this.searchInput.value, this.searchCategoryInput.value, [result], 1);
      }
    } else if (type === 'categories' && this.companiesList !== null) {
      result = this.searchCategoryResults.find(result => result.id === id);

      /** Search companies by category */
      this.searchInput.value = '';
      this.searchCategoryInput.value = String(result.id);
      this.searchCompanies('', String(result.id));
      // this.companiesList.unbindNavigation();
    }
  }

  /**
   * Show the results box with animation
   */
  show() {
    /** Hide the results box on click outside it */
    document.querySelector('body').addEventListener('click', this.onClickOutsideBox);

    /** Show the box */
    this.resultsElt.removeAttribute('hidden');
    setTimeout(() => {
      this.resultsElt.classList.remove('is-hidden');
    }, 10);
    if (!this.isSearchBoxOpen) {
      this.isSearchBoxOpen = true;
    }
  }

  /**
   * Hide the results box with animation
   */
  hide() {
    /** Unbind the click outside event */
    document.querySelector('body').removeEventListener('click', this.onClickOutsideBox);

    /** Hide the box */
    this.resultsElt.classList.add('is-hidden');
    setTimeout(() => {
      this.resultsElt.setAttribute('hidden', 'true');
    }, this.transitionDuration);
  }
}



// WEBPACK FOOTER //
// ./scripts/components/CompaniesSearch.js